import React, { useCallback, useState } from 'react';
import { ButtonDiv, Description, Form, ModalBottom, VerificationBox, VerificationCode } from './styles';
import Button from '../../button';
import { deleteWorkspace, sendDeleteRequest } from '../../../services/workspaceService';
import { captureException } from '../../../services/logService';
import { useHistory } from 'react-router-dom';
import { ROUTES } from '../../../routes/urls';
import { setWorkspaceIdInAxios } from '../../../helpers/authTokenHelpers';
import { workspaceSetAnalytics } from '../../../services/analyticsService';
import { isDev } from '../../../helpers/common';
import { updateUserPreference } from '../../../helpers/firebaseHelper';
import { clearPreviousWorkspaceData } from '../../../services/baseServices';
import { rootStore } from '../../../mobx/rootStore';
import UserPreferenceSingleton from '../../../helpers/userPreferenceSingleton';
import { BUTTON_TYPE } from '../../../global/constants';
import { observer } from 'mobx-react-lite';
import ModalHeader from '../modalHeader';
import Input from '../../input';

interface Props {
  onClose: () => void;
}

export const VerificationModal = observer(({ onClose }: Props) => {
  //States
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [loading, setLoading] = useState(false);

  //mobx state variables
  const { workspaceStore } = rootStore;
  const { workspaceList, currentWorkspace } = workspaceStore;
  const workspace_id = currentWorkspace?.id;

  //Other variable
  const history = useHistory();

  const handleDeleteWorkspace = useCallback(
    async (e: React.SyntheticEvent) => {
      e.preventDefault();
      try {
        setLoading(true);
        if (!verificationCode) {
          rootStore.alertNotificationStore.setErrorMessage('Please enter a verification code');

          return;
        }
        const result = await deleteWorkspace(workspace_id || '', verificationCode);
        if (result) {
          const nextWorkspace = workspaceList?.find((item) => item.id !== workspace_id);
          UserPreferenceSingleton.getInstance().setWorkspace(nextWorkspace || null);
          if (nextWorkspace) {
            setWorkspaceIdInAxios(nextWorkspace.id);
            workspaceSetAnalytics(nextWorkspace.id);
            if (!isDev()) await updateUserPreference({ current_workspace_id: nextWorkspace.id });
          }
          await clearPreviousWorkspaceData();
          // Select workspace page fetches the fresh list and runs appInit when a workspace is picked
          history.push(ROUTES.SELECT_WORKSPACE);
        }
      } catch (e) {
        captureException(e);
        rootStore.alertNotificationStore.setErrorMessage(
          'An error occurred while deleting the workspace. Please try again.'
        );
      } finally {
        setLoading(false);
      }
    },
    [verificationCode, workspace_id, workspaceList, history]
  );

  const handleResendCode = useCallback(async () => {
    try {
      await sendDeleteRequest(true);
    } catch (e) {
      captureException(e);
      rootStore.alertNotificationStore.setErrorMessage('Failed to resend verification code. Please try again.');
    }
  }, []);

  return (
    <>
      <ModalHeader title='Verification' onClose={onClose} />
      <ModalBottom>
        <Form>
          <Description>
            A verification code is sent to your Email Address. You need to enter it and get verified.
          </Description>
          <VerificationBox>
            <Input
              placeHolder='Enter Verification Code'
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
            />
            <VerificationCode role='button' onClick={handleResendCode}>
              Resend code
            </VerificationCode>{' '}
          </VerificationBox>
        </Form>
        <ButtonDiv>
          <Button title='Cancel' variant={BUTTON_TYPE.SECONDARY} onClick={onClose} isLoading={false} />
          <Button
            title='Delete workspace'
            buttonType='submit'
            variant={BUTTON_TYPE.PRIMARY}
            isLoading={loading}
            onClick={handleDeleteWorkspace}
          />
        </ButtonDiv>
      </ModalBottom>
    </>
  );
});
